import React, { useMemo, useCallback } from 'react';
import { faUserPlus, faRefresh, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useRole } from '../../context/roleContext';
import { USER_ROLES } from '../../utils/constants';
import { useTranslation } from 'react-i18next';
import ActionToolbar from '../common/ActionToolbar';

/**
 * OrdersActions
 * A simplified actions toolbar for Orders: search, add and refresh.
 */
const OrdersActions = ({
  showSearch = true,
  searchValue = '',
  onSearchChange,
  onAddOrder,
  onRefresh,
  loading = false,
  refreshLoading = false,
  // Bulk actions
  onBulkDelete,
  hasSelection = false,
  selectionCount = 0,
  onDeleteAll,
  statusValue = '',
  onStatusChange,
  fromDate = '',
  toDate = '',
  onFromDateChange,
  onToDateChange,
}) => {
  const { t } = useTranslation();
  const { userRole, checkDeleteAccess } = useRole();

  const handleAdd = useCallback(() => {
    onAddOrder?.();
  }, [onAddOrder]);

  const actions = useMemo(() => {
    const list = [
      {
        key: 'refresh-orders',
        label: t('ordersModule.common.buttons.refresh'),
        icon: faRefresh,
        onClick: onRefresh,
        disabled: refreshLoading,
        variant: 'ghost',
        showLabelBreakpoint: 'sm',
        spinner: refreshLoading,
      },
    ];

    if (userRole !== USER_ROLES.DELIVERY_SUPERVISOR) {
      list.push({
        key: 'add-order',
        label: t('ordersModule.orders.actions.buttons.add'),
        icon: faUserPlus,
        onClick: handleAdd,
        disabled: loading,
        variant: 'primary',
        showLabelBreakpoint: 'sm',
        loading,
      });
    }

    // Bulk Delete Action
    if (hasSelection && userRole === USER_ROLES.SUPER_ADMIN && checkDeleteAccess()) {
      list.push({
        key: 'bulk-delete',
        label: `Bulk Delete (${selectionCount})`,
        icon: faTrash,
        onClick: onBulkDelete,
        disabled: loading,
        variant: 'danger',
        showLabelBreakpoint: 'sm',
        loading,
      });
    }

    if (userRole === USER_ROLES.SUPER_ADMIN && checkDeleteAccess()) {
      list.push({
        key: 'delete-all',
        tooltip: 'Super Delete',
        icon: faTrash,
        onClick: onDeleteAll,
        disabled: loading,
        variant: 'danger',
        className: '!px-2 !py-1.5 !text-[10px]',
        loading,
      });
    }


    return list;
  }, [handleAdd, loading, onRefresh, refreshLoading, userRole, hasSelection, selectionCount, onBulkDelete, onDeleteAll, checkDeleteAccess, t]);

  const statusOptions = useMemo(() => [
    { value: 'all', label: t('commonCols.statusOptions.all') },
    { value: 'pending', label: t('commonCols.pending') },
    { value: 'confirmed', label: t('commonCols.statusOptions.confirmed') },
    { value: 'delivery_assigned', label: t('commonCols.statusOptions.deliveryAssigned') },
    { value: 'loading', label: t('commonCols.statusOptions.truckLoading') },
    { value: 'loaded', label: t('commonCols.statusOptions.truckLoaded') },
    { value: 'in_transit', label: t('commonCols.statusOptions.inTransit') },
    { value: 'delivered', label: t('commonCols.statusOptions.delivered') },
    { value: 'invoiced', label: t('commonCols.statusOptions.invoiced') },
  ], [t]);

  const filters = useMemo(() => ([
    {
      key: 'from-date',
      type: 'date',
      label: t('commonCols.from'),
      value: fromDate,
      onChange: onFromDateChange,
    },
    {
      key: 'to-date',
      type: 'date',
      label: 'To',
      value: toDate,
      onChange: onToDateChange,
    },
    {
      key: 'status-filter',
      type: 'select',
      label: t('commonCols.status'),
      value: statusValue,
      options: statusOptions,
      onChange: onStatusChange,
    },
  ]), [statusValue, onStatusChange, statusOptions, fromDate, toDate, onFromDateChange, onToDateChange]);

  return (
    <ActionToolbar
      showSearch={showSearch}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      searchPlaceholder={t('ordersModule.orders.actions.searchPlaceholder')}
      filters={filters}
      actions={actions}
    />
  );
};

export default OrdersActions;
